import React, { useState } from 'react';
import { useAppConfig } from './useAppConfig';
import { useTheme, fontFamily, ThemeButton } from './theme';

function App() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [user, setUser] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [showCustomRequest, setShowCustomRequest] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customYear, setCustomYear] = useState('');
  const [customType, setCustomType] = useState('movie');
  const [customNote, setCustomNote] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const config = useAppConfig();
  const { theme, darkMode, toggleDarkMode } = useTheme();

  const search = async (e) => {
    e.preventDefault();
    setHasSearched(true);
    setMessage('');
    setResults([]);
    setLoading(true);
    try {
      const res = await fetch(
        `${config.BACKEND_URL}/api/search?query=${encodeURIComponent(query)}`
      );
      const data = await res.json();
      setResults(data.results || []);
    } finally {
      setLoading(false);
    }
  };

  const requestselection = async (item) => {
  setRequesting(true);
  try {
    const res = await fetch(`${config.BACKEND_URL}/api/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user,
        note: customNote || note, // handle correctly for custom mode
        selection: {
          id: item.id,
          title: item.title || item.name,
          year: item.year,
          media_type: item.media_type,
        },
      }),
    });

    if (res.status === 409) {
      const data = await res.json();
      setMessage(data.error || 'This item has already been requested.');
      return;
    }

    setMessage(`requested selection: ${item.title || item.name}`);
    setNote('');
  } finally {
    setRequesting(false);
  }
};

  return (    
    <div
      style={{
        maxWidth: 1100,
        margin: '2.5rem auto',
        fontFamily,
        background: theme.bgColor,
        minHeight: '100vh',
        padding: '2rem 2rem 4rem 2rem',
        borderRadius: 18,
        boxShadow: theme.shadow,
        color: theme.text,
        transition: 'background 0.3s, color 0.3s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        {requesting && (
          <div style={{
            position: 'fixed', left: 0, top: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.08)', zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{
              background: theme.cardBg,
              padding: 32,
              borderRadius: 12,
              boxShadow: theme.shadow,
              display: 'flex',
              alignItems: 'center',
              fontSize: 20,
              color: theme.mainColor,
              fontWeight: 500
            }}>
              <span style={{
                width: 30, height: 30, marginRight: 16,
                border: `4px solid ${theme.mainColor}`,
                borderTop: `4px solid transparent`,
                borderRadius: '50%',
                display: 'inline-block',
                animation: 'spin 1s linear infinite',
              }} />
              Submitting request...
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg);}
                    100% { transform: rotate(360deg);}
                  }
                `}
              </style>
            </div>
          </div>
        )}
        <h1
          style={{
            color: theme.mainColor,
            fontWeight: 700,
            letterSpacing: '-1px',
            fontSize: 36,
            marginBottom: 0,
            flex: 1,
          }}
        >
          Movie Show Requester
        </h1>
        <a
          href="/admin"
          style={{
            background: theme.cardBg,
            color: theme.subtext,
            border: `1.5px solid ${theme.borderColor}`,
            borderRadius: 6,
            padding: '8px 18px',
            fontWeight: 600,
            fontSize: 15,
            textDecoration: 'none',
            boxShadow: '0 2px 8px 0 rgba(60,72,88,0.06)',
            transition: 'background 0.2s, color 0.2s',
          }}
          onMouseOver={(e) => { e.currentTarget.style.color = theme.mainColor; }}
          onMouseOut={(e) => { e.currentTarget.style.color = theme.subtext; }}
          title="Admin page"
        >
          Go to Admin
        </a>
        <button
          onClick={toggleDarkMode}
          style={{
            background: theme.cardBg,
            color: theme.text,
            border: `1.5px solid ${theme.borderColor}`,
            borderRadius: 6,
            padding: '8px 18px',
            fontWeight: 600,
            fontSize: 15,
            cursor: 'pointer',
            marginLeft: 16,
            boxShadow: '0 2px 8px 0 rgba(60,72,88,0.06)',
            transition: 'background 0.2s, color 0.2s',
          }}
          title="Toggle dark mode"
        >
          {darkMode ? '🌙 Dark' : '☀️ Light'}
        </button>
      </div>
      <p style={{ color: theme.subtext, marginBottom: 32 }}>
        Search for desired movie or show 
      </p>
      <form
        onSubmit={search}
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 32,
          alignItems: 'center',
        }}
      >
        <input
          placeholder="Your name (optional)"
          value={user}
          onChange={(e) => setUser(e.target.value)}
          style={{
            padding: '10px 14px',
            borderRadius: 6,
            border: `1px solid ${theme.borderColor}`,
            fontSize: 16,
            width: 180,
            background: theme.inputBg,
            color: theme.text,
            transition: 'background 0.2s, color 0.2s',
          }}
        />
        <input
          placeholder="Search for a movie or show"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          required
          style={{
            flex: 1,
            padding: '10px 14px',
            borderRadius: 6,
            border: `1px solid ${theme.borderColor}`,
            fontSize: 16,
            background: theme.inputBg,
            color: theme.text,
            transition: 'background 0.2s, color 0.2s',
          }}
        />
        <input
          placeholder="Add a note (optional)"
          value={note}
          onChange={e => setNote(e.target.value)}
          style={{
            padding: '10px 14px',
            borderRadius: 6,
            border: `1px solid ${theme.borderColor}`,
            fontSize: 16,
            width: 300,
            background: theme.inputBg,
            color: theme.text,
            marginLeft: 8,
            transition: 'background 0.2s, color 0.2s',
          }}
        />
        <button
          type="submit"
          style={{
            background: theme.buttonBg,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 6,
            padding: '10px 24px',
            fontWeight: 600,
            fontSize: 16,
            cursor: 'pointer',
            boxShadow: '0 2px 8px 0 rgba(25,118,210,0.08)',
            transition: 'background 0.2s',
          }}
        >
          Search
        </button>
      </form>
      {loading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          margin: '1.5rem 0',
          fontSize: 18,
          color: theme.mainColor,
          fontWeight: 500,
        }}>
          <span className="spinner" style={{
            width: 28,
            height: 28,
            border: `4px solid ${theme.mainColor}`,
            borderTop: `4px solid transparent`,
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'spin 1s linear infinite',
          }} />
          Searching...
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg);}
                100% { transform: rotate(360deg);}
              }
            `}
          </style>
        </div>
      )}
      {message && (
        <div
          style={{
            color: theme.successText,
            background: theme.successBg,
            borderRadius: 6,
            padding: '10px 16px',
            marginBottom: 24,
            fontWeight: 500,
          }}
        >
          {message}
        </div>
      )}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {results
        .filter((item) => item.media_type !== 'collection')
        .map((item) => {
          const title = item.title;
          const year = item.year;
          const posterUrl = item.poster_path && item.poster_path !== 'N/A'
            ? item.poster_path
            : '/Unknown_Poster_w80.png'; 
          const overview = item.overview;
          const mediaType = item.media_type === 'movie'
            ? 'Movie'
            : item.media_type === 'series'
              ? 'Show'
              : item.media_type;

          return (
            <li
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                margin: '1.5rem 0',
                background: theme.cardBg,
                border: '1px solid #e3e8ee',
                borderRadius: 10,
                boxShadow: '0 2px 8px 0 rgba(60,72,88,0.06)',
                padding: 18,
                gap: 20,
              }}
            >
              {posterUrl && (
                <img
                  src={posterUrl}
                  alt={title}
                  style={{
                    width: 80,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px 0 rgba(60,72,88,0.10)',
                    background: '#eee',
                  }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 22, color: theme.text, display: 'flex', alignItems: 'center', gap: 10 }}>
                  {title}
                  <span style={{ color: '#888', fontSize: 16 }}>({year})</span>
                  {item.inPlex && (
                    <span
                      style={{
                        display: 'inline-block',
                        background: '#ffc107',
                        color: '#222',
                        borderRadius: 4,
                        padding: '2px 8px',
                        fontWeight: 600,
                        fontSize: 13,
                        marginLeft: 10,
                        verticalAlign: 'middle',
                      }}
                    >
                      Already in Plex
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontStyle: 'italic',
                    color: '#1976d2',
                    margin: '4px 0 8px 0',
                    fontSize: 15,
                  }}
                >
                  {mediaType}
                </div>
                <div style={{ margin: '6px 0 12px 0', color: theme.subtext }}>
                  {overview}
                </div>
                <button
                  onClick={() => requestselection(item)}
                  style={{
                    background: '#1976d2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '8px 18px',
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px 0 rgba(25,118,210,0.08)',
                    transition: 'background 0.2s',
                  }}
                >
                  Request this
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {hasSearched && !loading && (
        <button
          style={{
            marginTop: 24,
            background: theme.buttonBg,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 6,
            padding: '10px 24px',
            fontWeight: 600,
            fontSize: 16,
            cursor: 'pointer',
            boxShadow: '0 2px 8px 0 rgba(25,118,210,0.08)',
            transition: 'background 0.2s',
          }}
          onClick={() => setShowCustomRequest(true)}
        >
          Submit a Custom Request
        </button>
      )}
      <hr
        style={{
          margin: '3rem 0',
          border: 'none',
          borderTop: `1px solid ${theme.borderColor}`,
        }}
      />
      {showCustomRequest && (
        <div style={{
          position: 'fixed', left: 0, right: 0, top: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: theme.cardBg,
            borderRadius: 10,
            padding: 32,
            maxWidth: 400,
            width: '100%',
            boxShadow: theme.shadow
          }}>
            <h2 style={{ color: theme.mainColor, marginBottom: 24 }}>Custom Movie/Show Request</h2>
            <form
              onSubmit={async e => {
                e.preventDefault();
                const selection = {
                  id: `custom-${Date.now()}`,
                  title: customTitle.trim(),
                  year: customYear.trim(),
                  media_type: customType,
                };
                // Use customNote as the note, or setNote if you want
                await requestselection(selection);
                setShowCustomRequest(false);
                setCustomTitle('');
                setCustomYear('');
                setCustomType('movie');
                setCustomNote('');
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500, display: 'block' }}>Type</label>
                <select
                  value={customType}
                  onChange={e => setCustomType(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    marginTop: 4,
                    borderRadius: 4,
                    border: `1px solid ${theme.borderColor}`,
                    fontSize: 16,
                    background: theme.inputBg,
                    color: theme.text,
                  }}
                >
                  <option value="movie">Movie</option>
                  <option value="series">TV Show</option>
                </select>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500, display: 'block' }}>Title</label>
                <input
                  type="text"
                  required
                  value={customTitle}
                  onChange={e => setCustomTitle(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    marginTop: 4,
                    borderRadius: 4,
                    border: `1px solid ${theme.borderColor}`,
                    fontSize: 16,
                    background: theme.inputBg,
                    color: theme.text,
                  }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500, display: 'block' }}>Year</label>
                <input
                  type="number"
                  min="1800"
                  max="2099"
                  step="1"
                  value={customYear}
                  onChange={e => setCustomYear(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    marginTop: 4,
                    borderRadius: 4,
                    border: `1px solid ${theme.borderColor}`,
                    fontSize: 16,
                    background: theme.inputBg,
                    color: theme.text,
                  }}
                  placeholder="e.g. 2023"
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 500, display: 'block' }}>Note (optional)</label>
                <input
                  type="text"
                  value={customNote}
                  onChange={e => setCustomNote(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    marginTop: 4,
                    borderRadius: 4,
                    border: `1px solid ${theme.borderColor}`,
                    fontSize: 16,
                    background: theme.inputBg,
                    color: theme.text,
                  }}
                  placeholder="Any extra info"
                />
              </div>
              <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
                <button
                  type="submit"
                  style={{
                    background: theme.buttonBg,
                    color: theme.buttonText,
                    border: 'none',
                    borderRadius: 6,
                    padding: '10px 24px',
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: 'pointer',
                  }}
                >
                  Submit
                </button>
                <button
                  type="button"
                  style={{
                    background: theme.deleteBg,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '10px 24px',
                    fontWeight: 600,
                    fontSize: 16,
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowCustomRequest(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <footer
        style={{
          marginTop: 48,
          color: theme.footer,
          textAlign: 'center',
          fontSize: 14,
        }}
      >
        &copy; {new Date().getFullYear()} Movie Show Requester
      </footer>
    </div>
  );
}

export default App;
